import { useState, useMemo, memo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettingsContext } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

import MenuItem from "./MenuItem";
import MobileMenuItem from "./MobileMenuItem";

import styles from "./Header.module.css";

interface HeaderProps {
	hideMarketingNav?: boolean;
}

const Header = memo(function Header({ hideMarketingNav = false }: HeaderProps) {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useAuth();
	const { settings } = useSettingsContext();

	const handlePortalClick = () => {
		if (user) {
			// Redirect based on user role
			const userRole = user.app_metadata?.role || user.user_metadata?.user_type;
			switch (userRole) {
				case "parent":
					navigate("/parent-dashboard");
					break;
				case "doula":
					navigate("/doula-dashboard");
					break;
				case "admin":
					navigate("/admin-dashboard");
					break;
				default:
					navigate("/login");
			}
		} else {
			navigate("/login");
		}
	};

	// Check if we're on search page to hide marketing nav
	const isSearchPage = location.pathname === "/search-doulas";
	const isDoulaProfilePage = location.pathname.startsWith("/doula-profile/");
	const isLoginPage = location.pathname === "/login";
	const isSignupPage = location.pathname === "/signup";
	const shouldHideMarketingNav =
		hideMarketingNav ||
		isSearchPage ||
		isDoulaProfilePage ||
		isLoginPage ||
		isSignupPage;

	const headerClasses = [
		styles.header,
		"bg-newTheme-cotton",
	];

	const menuItems = useMemo(
		() => settings?.doulaMenu.map((item) => ({
			id: item.id,
			title: item.menuName,
			href: item.menuLink,
		})) ?? [],
		[settings?.doulaMenu]
	);

	return (
		<header
			id={styles['header']}
			className={headerClasses.join(" ")}
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-0">
				<div className="flex justify-between items-center">
					{/* Logo - Clickable to go home */}
					<div className="flex items-center">
						{
							settings?.logoUrl && (
								<Link
									to="/"
									className="flex items-center space-x-2"
								>
									<img
										src={settings?.logoUrl ?? "/Gaia_Logo.svg"}
										alt={settings?.logoAlt ?? "GaiaBirth"}
										className={styles.logo}
										width={130}
										height={39}
									/>
								</Link>
							)
						}
					</div>

					{/* Desktop Navigation - Hidden on search page */}
					{(!shouldHideMarketingNav && menuItems.length > 0) && (
						<nav>
							<ul className="navItems hidden md:flex items-center space-x-8">
								{menuItems.map((item) => (
									<MenuItem
										key={item.id}
										title={item.title}
										href={item.href}
										hasSmoothScroll={true}
									/>
								))}
							</ul>
						</nav>
					)}

					{/* Desktop Action Buttons */}
					<div className="hidden md:flex items-center space-x-4">
						<Button
							variant="outline"
							onClick={handlePortalClick}
							className={`${styles.portalBtn} border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black`}
						>
							My Portal
						</Button>
						<Button
							onClick={() => navigate("/doula-signup")}
							className={`${styles.doulaBtn} bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow`}
						>
							Join as a Doula
						</Button>
					</div>

					{/* Mobile Menu Button */}
					<div className="md:hidden">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
							className="p-2"
						>
							{isMobileMenuOpen ? (
								<X className="w-6 h-6" />
							) : (
								<Menu className="w-6 h-6" />
							)}
						</Button>
					</div>
				</div>

				{/* Mobile Menu - Hidden on search page */}
				{isMobileMenuOpen && !shouldHideMarketingNav && (
					<div className="md:hidden border-t border-gray-200 py-4">
						{menuItems.length > 0 && (
							<nav className="flex flex-col space-y-4 px-4">
								<ul>
									{menuItems.map((item) => (
										<MobileMenuItem
											key={item.id}
											title={item.title}
											href={item.href}
											setIsMobileMenuOpen={setIsMobileMenuOpen}
										/>
									))}
								</ul>
								<div className="pt-4 border-t border-gray-200">
									<Button
										variant="outline"
										onClick={handlePortalClick}
										className="w-full mb-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-black"
									>
										My Portal
									</Button>
									<Button
										onClick={() => navigate("/doula-signup")}
										className="w-full bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
									>
										Join as a Doula
									</Button>
								</div>
							</nav>
						)}
					</div>
				)}

				{/* Mobile Menu for Search Page - Simplified but keeps Join as Doula */}
				{isMobileMenuOpen && shouldHideMarketingNav && (
					<div className="md:hidden bg-white border-t border-gray-200 py-4">
						<div className="flex flex-col space-y-4 px-4">
							<Button
								variant="outline"
								onClick={handlePortalClick}
								className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
							>
								My Portal
							</Button>
							<Button
								onClick={() => navigate("/doula-signup")}
								className="w-full bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
							>
								Join as a Doula
							</Button>
						</div>
					</div>
				)}
			</div>
		</header>
	)
});

export default Header;